/* ============================================================
   CART SHIP DATE RECOMPUTE
   ------------------------------------------------------------
   Recomputes the "Estimated Ship Date" on the cart page and
   cart drawer based on today + shipping method, replacing the
   frozen date stored on the line item at add-to-cart time.

   Also syncs the refreshed date back to Shopify's line item
   Shipping property via /cart/change.js so checkout and order
   confirmation reflect what the customer last saw.

   Date algorithm mirrors assets/diamond-customizer.js exactly:
     - Standard: +14 calendar days
     - Express:  +7 calendar days
     - Next Day Delivery: same day if before 2:45 PM ET (weekday),
       otherwise next weekday
   ============================================================ */

(function () {
  'use strict';

  // Defaults — overridden by shop.metafields.custom.shipping_config via #shipping-config script tag.
  // "Regular" is a legacy alias for "Standard" written by older customizer versions.
  const SHIP_DAYS = { Standard: 14, Regular: 14, Express: 7 };
  const SHIP_MODE = { Standard: 'calendar', Regular: 'calendar', Express: 'calendar' };
  let NEXT_DAY_CUTOFF_HOUR = 14;
  let NEXT_DAY_CUTOFF_MINUTE = 45;

  (function loadShippingConfig() {
    const script = document.getElementById('shipping-config');
    const raw = script && script.textContent && script.textContent.trim();
    if (!raw || raw === 'null') return;
    const isValidMode = function (m) { return m === 'calendar' || m === 'business'; };
    try {
      const cfg = JSON.parse(raw);
      if (!cfg || typeof cfg !== 'object') return;
      if (Number.isFinite(cfg.regular_days)) {
        SHIP_DAYS.Standard = cfg.regular_days;
        SHIP_DAYS.Regular  = cfg.regular_days;
      }
      if (isValidMode(cfg.regular_mode)) {
        SHIP_MODE.Standard = cfg.regular_mode;
        SHIP_MODE.Regular  = cfg.regular_mode;
      }
      if (Number.isFinite(cfg.express_days))           SHIP_DAYS.Express      = cfg.express_days;
      if (isValidMode(cfg.express_mode))               SHIP_MODE.Express      = cfg.express_mode;
      if (Number.isFinite(cfg.next_day_cutoff_hour))   NEXT_DAY_CUTOFF_HOUR   = cfg.next_day_cutoff_hour;
      if (Number.isFinite(cfg.next_day_cutoff_minute)) NEXT_DAY_CUTOFF_MINUTE = cfg.next_day_cutoff_minute;
    } catch (_) { /* fall back to defaults */ }
  })();

  function addBusinessDays(startDate, numDays) {
    const result = new Date(startDate);
    let added = 0;
    while (added < numDays) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return result;
  }
  const MONTHS_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const MONTHS_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  function ordinalSuffix(day) {
    const m10 = day % 10;
    const m100 = day % 100;
    if (m10 === 1 && m100 !== 11) return 'st';
    if (m10 === 2 && m100 !== 12) return 'nd';
    if (m10 === 3 && m100 !== 13) return 'rd';
    return 'th';
  }

  function computeFreshDate(method) {
    if (SHIP_DAYS[method] != null) {
      if (SHIP_MODE[method] === 'business') {
        return addBusinessDays(new Date(), SHIP_DAYS[method]);
      }
      const d = new Date();
      d.setDate(d.getDate() + SHIP_DAYS[method]);
      return d;
    }
    const d = new Date();
    if (method === 'Next Day Delivery') {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric', minute: 'numeric', weekday: 'short', hour12: false
        }).formatToParts(new Date());
        const hour = parseInt(parts.find(p => p.type === 'hour').value, 10);
        const minute = parseInt(parts.find(p => p.type === 'minute').value, 10);
        const weekday = parts.find(p => p.type === 'weekday').value;
        const currentMinutes = hour * 60 + minute;
        const cutoffMinutes = NEXT_DAY_CUTOFF_HOUR * 60 + NEXT_DAY_CUTOFF_MINUTE;
        const isWeekend = (weekday === 'Sat' || weekday === 'Sun');
        if (isWeekend || currentMinutes >= cutoffMinutes) {
          d.setDate(d.getDate() + 1);
          while (d.getDay() === 0 || d.getDay() === 6) {
            d.setDate(d.getDate() + 1);
          }
        }
        return d;
      } catch (_) {
        d.setDate(d.getDate() + 1);
        return d;
      }
    }
    return null;
  }

  function formatLong(d) {
    // e.g. "May 5th"
    const day = d.getDate();
    return MONTHS_LONG[d.getMonth()] + ' ' + day + ordinalSuffix(day);
  }

  function formatShort(d) {
    // e.g. "May 5" — matches PDP's toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return MONTHS_SHORT[d.getMonth()] + ' ' + d.getDate();
  }

  // Guard against concurrent syncs for the same line + infinite loops from MutationObserver
  const syncingKeys = new Set();
  const justSyncedKeys = new Map(); // key -> freshShipping string

  function syncToShopify(key, propertiesArray, method, freshDate) {
    if (!key || syncingKeys.has(key)) return;
    // 'Regular' is normalized to 'Standard' on write so all new lines speak the customer-facing label.
    const writeLabel = (method === 'Regular') ? 'Standard' : method;
    const newShipping = writeLabel + ' - ' + formatShort(freshDate);
    // Rebuild properties object from [[k,v],...] pairs
    const props = {};
    for (let i = 0; i < propertiesArray.length; i++) {
      const pair = propertiesArray[i];
      if (pair && pair.length === 2) props[pair[0]] = pair[1];
    }
    if (props.Shipping === newShipping) return; // no drift
    props.Shipping = newShipping;
    // Stamp _AddedAt on first sync so future drift checks work for non-customizer lines.
    if (!props._AddedAt) {
      props._AddedAt = new Date().toISOString().split('T')[0];
    }

    // Skip if we already synced this exact value for this key
    if (justSyncedKeys.get(key) === newShipping) return;

    syncingKeys.add(key);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, properties: props })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { /* silent */ })
      .then(function () {
        justSyncedKeys.set(key, newShipping);
        syncingKeys.delete(key);
      });
  }

  function refreshOne(el) {
    const method = el.dataset.shipMethod || 'Standard';

    const fresh = computeFreshDate(method);
    if (!fresh) return;

    // 1. Update the visible text (no-op on same-day visits when values match)
    const span = el.querySelector('.ship-date-text');
    if (span) {
      const newText = formatLong(fresh);
      if (span.textContent !== newText) {
        span.textContent = newText;
      }
    }

    let propsArr;
    try {
      propsArr = JSON.parse(el.dataset.itemProperties || '[]');
    } catch (_) {
      return;
    }

    // 2. Decide whether to sync the Shipping property back to Shopify.
    //    Staleness is judged from the DATE ALREADY ON THE LINE, never from `_AddedAt`.
    //
    //    WHY NOT `_AddedAt` (2026-08-18): it is written only by the PDP customizer's
    //    syncShippingFormProperties, so a line can legitimately carry Shipping with
    //    no stamp — order 11168 is one, with every raw property still intact. The
    //    old guard read a missing stamp as "legacy line, display-only" and returned,
    //    which left every such line UNREFRESHABLE FOR THE LIFE OF THE CART. Order
    //    11169 was placed on Aug 17 carrying "Express - Aug 13" — a promise date
    //    four days in the PAST, frozen when the basket was built on Aug 6 and never
    //    corrected, because there was no stamp to prove it had drifted.
    //
    //    The stored date answers "is this stale?" on its own, so no stamp is needed.
    //    Comparing DATES ONLY (never the whole string) also leaves a line carrying
    //    the legacy "Regular" label untouched while its date is still correct,
    //    instead of rewriting it to "Standard" on every single cart view.
    //    Lines with no Shipping property at all still fall through and get stamped.
    const storedShipping = (propsArr.find(function (p) { return p && p[0] === 'Shipping'; }) || [])[1];
    if (storedShipping) {
      const storedDate = String(storedShipping).split(' - ').slice(1).join(' - ').trim();
      if (storedDate && storedDate === formatShort(fresh)) return; // already current
    }
    syncToShopify(el.dataset.itemKey, propsArr, method, fresh);
  }

  function refreshAll() {
    const nodes = document.querySelectorAll('.js-estimated-ship-date');
    for (let i = 0; i < nodes.length; i++) {
      refreshOne(nodes[i]);
    }
    refreshFooterAggregate();
  }

  function refreshFooterAggregate() {
    const el = document.querySelector('.bc-ship-date');
    if (!el) return;
    // The footer ship date reflects when the LAST line in the order is ready —
    // the order isn't complete until the slowest item ships.
    const lineEls = document.querySelectorAll('.js-estimated-ship-date');
    let latest = null;
    for (let i = 0; i < lineEls.length; i++) {
      const m = lineEls[i].dataset.shipMethod || 'Standard';
      const d = computeFreshDate(m);
      if (d && (!latest || d > latest)) latest = d;
    }
    if (!latest) latest = computeFreshDate('Standard');
    if (!latest) return;
    const text = latest.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
    // Write only on change: assigning textContent always swaps the text node, which
    // the #main-cart-footer observer below saw as a re-render and re-ran this every
    // 50 ms forever. Time-based rolling is now done by the clock refresh below.
    if (el.textContent !== text) el.textContent = text;
  }

  // Debounced refresh for mutation bursts
  let refreshTimer = null;
  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshAll, 50);
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAll);
  } else {
    refreshAll();
  }

  // Re-run after cart re-renders (Dawn replaces innerHTML of cart-items / cart-drawer on updates)
  const observerTargets = ['main-cart-items', 'main-cart-footer', 'CartDrawer-CartItems'];
  observerTargets.forEach(function (id) {
    const root = document.getElementById(id);
    if (!root) return;
    new MutationObserver(scheduleRefresh).observe(root, { childList: true, subtree: true });
  });

  // Clock refresh: rolls dates past the Next Day cutoff and midnight, and re-syncs
  // the Shipping property, on the cart page (where the old self-triggering loop ran).
  // Every 60 s, plus at once when the tab comes back or the page is restored from bfcache.
  function clockRefresh() {
    if (document.querySelector('#main-cart-footer .bc-ship-date')) refreshAll();
  }
  setInterval(clockRefresh, 60000);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') clockRefresh();
  });
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) clockRefresh();
  });
})();
